import { ProductSpecifications } from '@app/entities/Product/module/types/productSchema';
import { useAppDispatch, useAppSelector } from '@app/redux/hooks';
import { filterActions, getParams, getTestId } from '@entities/Filter';
import { useMemo } from 'react';
import { dataIdByName } from '../const/const-params';

import { FormBlockElement, Option, TypeElement } from '../types/filterElementsSchema';
import { useRouter } from 'next/router';

export interface DefaultSelectType {
  type: TypeElement;
  name: string;
  title: string;
  isInfoTooltip: boolean;
  dataId: string;
  defaultValue: string;
  data: string[];
  disabled: boolean;
  onChange: any;
}

export interface RangeSelectType {
  type: TypeElement;
  name: string;
  title: string;
  isInfoTooltip: boolean;
  min: {
    disabled: boolean;
    dataId?: string;
    defaultValue: any;
    data: string[];
    onChange: any;
  };
  max: {
    disabled: boolean;
    dataId?: string;
    defaultValue: any;
    data: string[];
    onChange: any;
  };
}

interface ReturnManagerSelects {
  selects: Array<DefaultSelectType | RangeSelectType>;
}

export const useManagerSelects = (elements: Array<FormBlockElement<ProductSpecifications | ProductSpecifications[]>>): ReturnManagerSelects => {
  const dispatch = useAppDispatch();
  const params = useAppSelector(getParams);
  const router = useRouter();

  const generateRangeOptions = (fullOptions: Option[], minValue, maxValue) => {
    let minOptions = fullOptions;
    let maxOptions = fullOptions;
    const indexMinValue = fullOptions.findIndex(value => value.value + '' === minValue + '');
    const indexMaxValue = fullOptions.findIndex(value => value.value + '' === maxValue + '');
    if (minValue && maxValue) {
      minOptions = fullOptions.slice(0, indexMaxValue + 1);
      maxOptions = fullOptions.slice(indexMinValue);

      return { minOptions, maxOptions };
    }

    if (minValue && !maxValue) {
      minOptions = fullOptions;
      maxOptions = fullOptions.slice(indexMinValue);

      return { minOptions, maxOptions };
    }

    if (!minValue && maxValue) {
      minOptions = fullOptions.slice(0, indexMaxValue + 1);
      maxOptions = fullOptions;

      return { minOptions, maxOptions };
    }

    return { minOptions, maxOptions };
  };

  const selects = useMemo(() => {
    return elements.map(element => {
      if (element.type === TypeElement.Select) {
        const name = element.name as ProductSpecifications;
        return {
          type: element.type,
          title: element.title,
          name,
          data: [element.nullState || 'Все', ...[...element.options].map(x => x.title + '')],
          defaultValue: params[name] || element.nullState || 'Все',
          isInfoTooltip: element.isInfoTooltip,
          onChange: (value: string) =>
            dispatch(
              filterActions.setListParams([
                {
                  key: name,
                  value: element.options.find(x => x.title === value)?.value || null,
                },
              ]),
            ),
          dataId: getTestId(name),
          disabled: element.disabled,
        };
      } else if (element.type === TypeElement.RangeSelects) {
        const name = element.name as ProductSpecifications[];
        const { minOptions, maxOptions } = generateRangeOptions([...element.options], params[name[0]], params[name[1]]);
        return {
          type: element.type,
          isInfoTooltip: element.isInfoTooltip,
          name: name[0],
          title: element.title,
          min: {
            name: name[0],
            data: [element.nullState || 'Все', ...minOptions.map(x => x.title + '')],
            defaultValue: params[name[0]] || element.nullState || 'Все',
            onChange: (value: string) =>
              dispatch(
                filterActions.setListParams([
                  {
                    key: name[0],
                    value: element.options.find(x => x.title === value)?.value || null,
                  },
                ]),
              ),
            dataId: getTestId(name[0]),
            disabled: element.disabled,
          },
          max: {
            title: element.title,
            name: name[1],
            data: ['Все', ...maxOptions.map(x => x.title + '')],
            defaultValue: params[name[1]] || element.nullState || 'Все',
            onChange: (value: string) =>
              dispatch(
                filterActions.setListParams([
                  {
                    key: name[1],
                    value: element.options.find(x => x.title === value)?.value || null,
                  },
                ]),
              ),
            dataId: getTestId(name[1]),
            disabled: element.disabled,
          },
        };
      } else {
        return null;
      }
    });
  }, [params, router]);

  return {
    selects,
  };
};
